import type { FC } from "react";
import styled from "@emotion/styled";
import { Fragment, useCallback, useMemo } from "react";
import {
  LiveEmptyStateCard,
  renderSmartCard,
  type RenderSmartCardOptions,
} from "@/components/heads-up/cards";
import { SmartCardKind } from "@/components/heads-up/heads-up.types";
import type { SmartCardRow } from "@/domains/tanstack-db";
import { useHeadsUpAnalytics } from "@/components/heads-up/analytics";

interface Props {
  cards: SmartCardRow[];
  cardRenderOptions: RenderSmartCardOptions;
  isLiveActive?: boolean;
}

export const HeadsUpContentView: FC<Props> = ({
  cards,
  cardRenderOptions,
  isLiveActive = false,
}) => {
  const analytics = useHeadsUpAnalytics();

  const buildSectionAnalyticsHandlers = useCallback(
    (sectionPositionIndex: number) => ({
      sectionPositionIndex,
      onItemPreviewOpen: analytics.trackHover,
      onItemClick: analytics.trackClick,
    }),
    [analytics]
  );

  const hasLiveCards = useMemo(
    () => cards.some(card => card.derived_kind === SmartCardKind.Live),
    [cards]
  );
  const shouldShowLiveEmptyState = isLiveActive && !hasLiveCards;

  const liveEmptyInsertionIndex = useMemo(() => {
    if (!shouldShowLiveEmptyState) return -1;

    const lastMeetingBriefingIndex = cards.reduce((lastIndex, card, index) => {
      if (card.derived_kind === SmartCardKind.MeetingBriefing) return index;
      return lastIndex;
    }, -1);

    if (lastMeetingBriefingIndex >= 0) return lastMeetingBriefingIndex + 1;
    return 0;
  }, [cards, shouldShowLiveEmptyState]);

  return (
    <CardList>
      {cards.map((card, index) => {
        const content = renderSmartCard(card, {
          ...cardRenderOptions,
          isLiveActive,
          analytics: buildSectionAnalyticsHandlers(index),
        });

        if (!content) return null;

        return (
          <Fragment key={card.id}>
            {index === liveEmptyInsertionIndex ? (
              <CardItem>
                <CardContent>
                  <LiveEmptyStateCard isLiveActive={isLiveActive} />
                </CardContent>
              </CardItem>
            ) : null}
            <CardItem>
              <CardContent>{content}</CardContent>
            </CardItem>
          </Fragment>
        );
      })}

      {liveEmptyInsertionIndex === cards.length ? (
        <CardItem>
          <CardContent>
            <LiveEmptyStateCard isLiveActive={isLiveActive} />
          </CardContent>
        </CardItem>
      ) : null}
    </CardList>
  );
};

const CardList = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["1"]};
`;
